/**
 * VAT Returns API
 * 
 * API methods for VAT (ALV) returns management.
 */

import { apiClient } from './client';
import type {
  VatReturn,
  VatReturnListResponse,
  VatReturnCalculation,
  CreateVatReturnRequest,
  VatPeriod,
} from './types';

/**
 * List VAT returns for an organization
 */
export async function listVatReturns(
  organizationId: string,
  options?: { status?: string; limit?: number; offset?: number }
): Promise<VatReturnListResponse> {
  const params = new URLSearchParams();
  params.set('organization_id', organizationId);
  if (options?.status) params.set('status', options.status);
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.offset) params.set('offset', options.offset.toString());

  return apiClient.get<VatReturnListResponse>(`/vat-returns?${params.toString()}`);
}

/**
 * Create a new VAT return
 */
export async function createVatReturn(
  data: CreateVatReturnRequest
): Promise<{ id: string; message: string }> {
  return apiClient.post<{ id: string; message: string }>('/vat-returns', data);
}

/**
 * Get a specific VAT return
 */
export async function getVatReturn(id: string): Promise<VatReturn> {
  return apiClient.get<VatReturn>(`/vat-returns/${id}`);
}

/**
 * Calculate VAT return for a period
 */
export async function calculateVatReturn(
  id: string
): Promise<VatReturnCalculation> {
  return apiClient.post<VatReturnCalculation>(`/vat-returns/${id}/calculate`, {});
}

/**
 * Preview VAT return calculation without saving
 */
export async function previewVatCalculation(
  organizationId: string,
  periodStart: string,
  periodEnd: string,
  reportingBasis: 'accrual' | 'cash' = 'accrual'
): Promise<VatReturnCalculation> {
  return apiClient.post<VatReturnCalculation>('/vat-returns/preview', {
    organization_id: organizationId,
    period_start: periodStart,
    period_end: periodEnd,
    reporting_basis: reportingBasis,
  });
}

/**
 * File a VAT return (mark as filed)
 */
export async function fileVatReturn(
  id: string
): Promise<{ success: boolean; message: string }> {
  return apiClient.post<{ success: boolean; message: string }>(`/vat-returns/${id}/file`, {});
}

/**
 * Delete a draft VAT return
 */
export async function deleteVatReturn(
  id: string
): Promise<{ success: boolean; message: string }> {
  return apiClient.delete<{ success: boolean; message: string }>(`/vat-returns/${id}`);
}

/**
 * Get VAT periods for calendar view
 */
export function getVatPeriods(_organizationId: string): VatPeriod[] {
  // Generate VAT periods for the current and previous year
  // Finnish VAT periods: monthly, quarterly, or yearly
  // For simplicity, we'll generate quarterly periods
  const periods: VatPeriod[] = [];
  const now = new Date();
  const currentYear = now.getFullYear();

  // Generate for current and previous year
  for (let year = currentYear - 1; year <= currentYear; year++) {
    // Q1: Jan-Mar
    periods.push({
      startDate: `${year}-01-01`,
      endDate: `${year}-03-31`,
      label: `Q1 ${year}`,
      dueDate: `${year}-04-12`,
      status: getPeriodStatus(`${year}-03-31`, `${year}-04-12`),
    });

    // Q2: Apr-Jun
    periods.push({
      startDate: `${year}-04-01`,
      endDate: `${year}-06-30`,
      label: `Q2 ${year}`,
      dueDate: `${year}-07-12`,
      status: getPeriodStatus(`${year}-06-30`, `${year}-07-12`),
    });

    // Q3: Jul-Sep
    periods.push({
      startDate: `${year}-07-01`,
      endDate: `${year}-09-30`,
      label: `Q3 ${year}`,
      dueDate: `${year}-10-14`,
      status: getPeriodStatus(`${year}-09-30`, `${year}-10-14`),
    });

    // Q4: Oct-Dec
    periods.push({
      startDate: `${year}-10-01`,
      endDate: `${year}-12-31`,
      label: `Q4 ${year}`,
      dueDate: `${year + 1}-02-28`,
      status: getPeriodStatus(`${year}-12-31`, `${year + 1}-02-28`),
    });
  }

  // Sort by start date descending (newest first)
  return periods
    .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
    .filter(p => new Date(p.startDate) <= now);
}

function getPeriodStatus(endDate: string, dueDate: string): 'upcoming' | 'current' | 'overdue' | 'filed' {
  const now = new Date();
  const end = new Date(endDate);
  const due = new Date(dueDate);

  if (now > due) {
    return 'overdue';
  }
  if (now >= end && now <= due) {
    return 'current';
  }
  return 'upcoming';
}

/**
 * Export VAT return as PDF
 */
export function getVatReturnPdfUrl(id: string): string {
  return `${apiClient['baseUrl']}/vat-returns/${id}/pdf`;
}

/**
 * Export VAT return for MyTax/ilmoitin.fi
 */
export async function exportVatReturnForMyTax(
  id: string
): Promise<{ data: string; format: string }> {
  return apiClient.get<{ data: string; format: string }>(`/vat-returns/${id}/export/mytax`);
}

// VAT Breakdown Types
export interface VatBreakdownItem {
  vatRate: number;
  vatCode: string;
  description: string;
  sales: {
    amountExclVat: number;
    vatAmount: number;
  };
  purchases: {
    amountExclVat: number;
    vatAmount: number;
  };
}

export interface VatBreakdownSummary {
  totalSalesExclVat: number;
  totalSalesVat: number;
  totalPurchasesExclVat: number;
  totalPurchasesVat: number;
  vatPayable: number;
  vatDeductible: number;
  netVat: number;
}

export interface VatBreakdownResponse {
  vatReturn: {
    id: string;
    periodStart: string;
    periodEnd: string;
    status: string;
    reportingBasis: string;
  };
  breakdown: VatBreakdownItem[];
  summary: VatBreakdownSummary;
}

/**
 * Get detailed VAT breakdown by tax rate
 */
export async function getVatBreakdown(
  id: string,
  organizationId: string
): Promise<VatBreakdownResponse> {
  return apiClient.get<VatBreakdownResponse>(`/vat-returns/${id}/breakdown?organization_id=${organizationId}`);
}

/**
 * Recalculate VAT return
 */
export async function recalculateVatReturn(
  id: string,
  organizationId: string
): Promise<{ message: string; return: VatReturn }> {
  return apiClient.post<{ message: string; return: VatReturn }>(`/vat-returns/${id}/recalculate?organization_id=${organizationId}`, {});
}
